import { setVenueStripeToken } from 'actions/venue';
import {
  FetchConnectedAccountDetailsAPIRequest,
  FetchSubscriptionSourceAPIRequest,
  ModifySourceAPIRequest,
  PayoutsAPIRequest,
  PayoutTransactionsAPIRequest,
  UpdateFinancialDetailsAPIRequest,
  UploadStripeDocumentAPIRequest,
} from 'utils/constants/network';
import { callAPI } from 'utils/network';

export const REQUEST_PAYOUTS = 'REQUEST_PAYOUTS';
export const SET_PAYOUT_LIST = 'SET_PAYOUT_LIST';
export const REQUEST_PAYOUT_TRANSACTIONS = 'REQUEST_PAYOUT_TRANSACTIONS';
export const SET_PAYOUT_TRANSACTIONS_LIST = 'SET_PAYOUT_TRANSACTIONS_LIST';
export const SET_UPLOADED_STRIPE_FILE = 'SET_UPLOADED_STRIPE_FILE';
export const REQUEST_STRIPE_FILE_UPLOAD = 'REQUEST_STRIPE_FILE_UPLOAD';
export const REQUEST_CONNECTED_ACCOUNT_DETAILS =
  'REQUEST_CONNECTED_ACCOUNT_DETAILS';
export const SET_CONNECTED_ACCOUNT_DETAILS = 'SET_CONNECTED_ACCOUNT_DETAILS';
export const REQUEST_UPDATE_FINANCIAL_DETAILS =
  'REQUEST_UPDATE_FINANCIAL_DETAILS';
export const FINANCIAL_DETAILS_UPDATED = 'FINANCIAL_DETAILS_UPDATED';
export const REQUEST_MODIFY_SOURCE = 'REQUEST_MODIFY_SOURCE';
export const DID_MODIFY_SOURCE = 'DID_MODIFY_SOURCE';
export const REQUEST_FETCH_SUBSCRIPTION_SOURCE =
  'REQUEST_FETCH_SUBSCRIPTION_SOURCE';
export const DID_FETCH_SUBSCRIPTION_SOURCE = 'DID_FETCH_SUBSCRIPTION_SOURCE';
export const CLEAR_SUBSCRIPTION_SOURCES = 'CLEAR_SUBSCRIPTION_SOURCES';

const requestPayouts = () => ({
  type: REQUEST_PAYOUTS,
  payload: {
    fetchingPayouts: true,
    payoutsFetched: false,
  },
});

const setPayoutList = (list, success) => ({
  type: SET_PAYOUT_LIST,
  payload: {
    fetchingPayouts: false,
    payoutsFetched: success,
    payoutList: list,
  },
});

const requestPayoutTransactions = () => ({
  type: REQUEST_PAYOUT_TRANSACTIONS,
  payload: {
    fetchingPayoutTransactions: true,
    payoutTransactionsFetched: false,
  },
});

const setPayoutTransactionsList = (list, success) => ({
  type: SET_PAYOUT_TRANSACTIONS_LIST,
  payload: {
    fetchingPayoutTransactions: false,
    payoutTransactionsFetched: success,
    payoutTransactionsList: list,
  },
});

const requestConnectedAccountDetails = () => ({
  type: REQUEST_CONNECTED_ACCOUNT_DETAILS,
  payload: {
    fetchingConnectedAccount: true,
    connectedAccount: null,
    connectedAccountError: null,
  },
});

const requestUpdateFinancialDetails = () => ({
  type: REQUEST_UPDATE_FINANCIAL_DETAILS,
  payload: {
    updatingFinancialDetails: true,
    updateFinancialDetailsError: null,
  },
});

export const financialDetailsUpdated = err => ({
  type: FINANCIAL_DETAILS_UPDATED,
  payload: {
    updatingFinancialDetails: false,
    updateFinancialDetailsError: err,
  },
});

export const setConnectedAccountDetails = (details, err) => ({
  type: SET_CONNECTED_ACCOUNT_DETAILS,
  payload: {
    fetchingConnectedAccount: false,
    connectedAccount: details,
    connectedAccountError: err,
  },
});

export const setUploadedStripeFile = (file, err) => ({
  type: SET_UPLOADED_STRIPE_FILE,
  payload: {
    uploadedFileContents: file,
    uploadingFile: false,
    fileUploadError: err,
  },
});

const requestStripeFileUpload = () => ({
  type: REQUEST_STRIPE_FILE_UPLOAD,
  payload: {
    uploadedFileContents: null,
    uploadingFile: true,
    fileUploadError: null,
  },
});

const requestModifySource = () => ({
  type: REQUEST_MODIFY_SOURCE,
  payload: {
    modifyingSource: true,
    modifyingSourceError: null,
  },
});

const didModifySource = (json, err) => ({
  type: DID_MODIFY_SOURCE,
  payload: {
    modifyingSource: false,
    modifyingSourceError: err,
  },
});

const requestFetchSubscriptionSource = () => ({
  type: REQUEST_FETCH_SUBSCRIPTION_SOURCE,
  payload: {
    fetchingSource: true,
    fetchingSourceError: null,
  },
});

const didFetchSubscriptionSource = (json, err) => ({
  type: DID_FETCH_SUBSCRIPTION_SOURCE,
  payload: {
    fetchingSource: false,
    fetchingSourceError: err,
    subscriptionDetails: json,
  },
});

export const clearSubscriptionSources = () => ({
  type: CLEAR_SUBSCRIPTION_SOURCES,
  payload: {
    subscriptionDetails: null,
  },
});

export const uploadStripeDocument = (base64, purpose) => {
  return dispatch => {
    dispatch(requestStripeFileUpload());

    const apiRequest = UploadStripeDocumentAPIRequest({
      file_base64_data: base64,
      purpose: purpose,
    });

    return callAPI(apiRequest)
      .then(json => dispatch(setUploadedStripeFile(json.file, null)))
      .catch(err => dispatch(setUploadedStripeFile(null, err)));
  };
};

export const modifySource = (modification, source, userID) => {
  return dispatch => {
    dispatch(requestModifySource());

    const apiRequest = ModifySourceAPIRequest({
      modification: modification,
      source: source,
      user_id: userID,
    });

    return callAPI(apiRequest)
      .then(json => dispatch(didModifySource(json, null)))
      .catch(err => dispatch(didModifySource(null, err)));
  };
};

export const fetchSubscriptionSource = custID => {
  return dispatch => {
    dispatch(requestFetchSubscriptionSource());

    const apiRequest = FetchSubscriptionSourceAPIRequest({
      customer_id: custID,
    });

    return callAPI(apiRequest)
      .then(json => dispatch(didFetchSubscriptionSource(json, null)))
      .catch(err => dispatch(didFetchSubscriptionSource(null, err)));
  };
};

export const fetchConnectedAccountDetails = () => {
  return async dispatch => {
    dispatch(requestConnectedAccountDetails());

    const apiRequest = FetchConnectedAccountDetailsAPIRequest({});

    try {
      const json = await callAPI(apiRequest);
      dispatch(setConnectedAccountDetails(json, null));
    } catch (err) {
      dispatch(setConnectedAccountDetails(null, err));
    }
  };
};

export const updateFinancialDetails = (stripeAcct, values) => {
  return dispatch => {
    dispatch(requestUpdateFinancialDetails());

    const apiRequest = UpdateFinancialDetailsAPIRequest({
      stripe_acct: stripeAcct,
      financials: values,
    });

    return callAPI(apiRequest)
      .then(json => {
        dispatch(setVenueStripeToken(json.id));
        dispatch(financialDetailsUpdated(null));
      })
      .catch(err => {
        dispatch(financialDetailsUpdated(err));
      });
  };
};

export const fetchPayouts = (
  stripeAccount,
  limit,
  startingAfter,
  endingBefore
) => {
  return dispatch => {
    dispatch(requestPayouts());

    const apiRequest = PayoutsAPIRequest({
      stripe_acct: stripeAccount,
      limit: limit,
      starting_after: startingAfter,
      ending_before: endingBefore,
    });

    return callAPI(apiRequest)
      .then(json => {
        const isEndingBefore =
          endingBefore !== undefined && endingBefore !== '';
        dispatch(
          setPayoutList(
            isEndingBefore ? json.payouts.reverse() : json.payouts,
            true
          )
        );
      })
      .catch(err => dispatch(setPayoutList([], false)));
  };
};

export const fetchPayoutTransactions = (
  stripeAccount,
  payoutID,
  limit,
  startingAfter,
  endingBefore
) => {
  return dispatch => {
    dispatch(requestPayoutTransactions());

    const apiRequest = PayoutTransactionsAPIRequest({
      stripe_acct: stripeAccount,
      payout: payoutID,
      limit: limit,
      starting_after: startingAfter,
      ending_before: endingBefore,
    });

    return callAPI(apiRequest)
      .then(json => {
        const isEndingBefore =
          endingBefore !== undefined && endingBefore !== '';
        dispatch(
          setPayoutTransactionsList(
            isEndingBefore ? json.transactions.reverse() : json.transactions,
            true
          )
        );
      })
      .catch(err => dispatch(setPayoutTransactionsList([], false)));
  };
};



// WEBPACK FOOTER //
// ./src/actions/financial/index.js